import React, { useEffect, forwardRef, useImperativeHandle, useRef  } from 'react';


const NormalBillPrint = forwardRef(({
  invoiceNumber,
  selectedPatient,
  selectedDoctor,
  date,
  products,
  balance_due,
  calculateTotal,
  grand_total,
  setTotalPages,
  currentPage, 
  generalSetting,
  username,
  payingAmount,
  sysInfo
}, ref) => {
  const productsPerPage = 8; 
  const barcodeRef = useRef(null);

  const validProducts = products.filter(product => product.id && product.name);

  const totalPages = Math.ceil(validProducts.length / productsPerPage);

  useEffect(() => {
    setTotalPages(totalPages);
  }, [products]);

  const paginatedProducts = products.slice((currentPage - 1) * productsPerPage, currentPage * productsPerPage);
  
  const isElectron = () => {
    return window && window.process && window.process.type;
  };

  let ipcRenderer;

  if (isElectron()) {
    ipcRenderer = window.require('electron').ipcRenderer;
  }


    useEffect(() => {
        if (barcodeRef.current && invoiceNumber) {
            // Load JsBarcode from CDN
            const script = document.createElement('script');
            script.src = 'https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js';
            script.async = true;
            script.onload = () => {
                if (window.JsBarcode) {
                    window.JsBarcode(barcodeRef.current, invoiceNumber, {
                        format: 'CODE128',
                        width: 1.5,
                        height: 25,
                        displayValue: false,
                        margin: 0
                    });
                }
            };
            document.body.appendChild(script);

            return () => {
                if (document.body.contains(script)) {
                    document.body.removeChild(script);
                }
            };
        }
    }, [invoiceNumber]);


  // Convert number to words (Indian format)
  const numberToWords = (num) => {
    if (num === 0) return "Zero Rupees Only";
    
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 
                  'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 
                  'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    
    const convertHundreds = (n) => {
      if (n === 0) return '';
      let result = '';
      if (n >= 100) {
        result += ones[Math.floor(n / 100)] + ' Hundred ';
        n %= 100;
        if (n > 0) result += 'and ';
      }
      if (n >= 20) {
        result += tens[Math.floor(n / 10)] + ' ';
        n %= 10;
      }
      if (n > 0) {
        result += ones[n] + ' ';
      }
      return result.trim();
    };
    
    const numStr = parseFloat(num).toFixed(2);
    const parts = numStr.split('.');
    const rupees = parseInt(parts[0]);
    const paise = parseInt(parts[1] || 0);
    
    let words = '';
    
    if (rupees === 0) {
      words = 'Zero';
    } else if (rupees < 100) {
      words = convertHundreds(rupees);
    } else if (rupees < 1000) {
      words = convertHundreds(rupees);
    } else if (rupees < 100000) {
      const thousands = Math.floor(rupees / 1000);
      const remainder = rupees % 1000;
      
      if (thousands > 0) {
        words = convertHundreds(thousands) + ' Thousand';
        if (remainder > 0) {
          words += ' ' + convertHundreds(remainder);
        }
      } else {
        words = convertHundreds(remainder);
      }
    } else if (rupees < 10000000) {
      const lakhs = Math.floor(rupees / 100000);
      const remainder = rupees % 100000;
      
      words = convertHundreds(lakhs) + ' Lakh';
      
      if (remainder > 0) {
        if (remainder >= 1000) {
          const thousands = Math.floor(remainder / 1000);
          const remainder2 = remainder % 1000;
          words += ' ' + convertHundreds(thousands) + ' Thousand';
          if (remainder2 > 0) {
            words += ' ' + convertHundreds(remainder2);
          }
        } else {
          words += ' ' + convertHundreds(remainder);
        }
      }
    } else {
      const crores = Math.floor(rupees / 10000000);
      const remainder = rupees % 10000000;
      
      words = convertHundreds(crores) + ' Crore';
      
      if (remainder > 0) {
        if (remainder >= 100000) {
          const lakhs = Math.floor(remainder / 100000);
          const remainder2 = remainder % 100000;
          words += ' ' + convertHundreds(lakhs) + ' Lakh';
          
          if (remainder2 > 0) {
            if (remainder2 >= 1000) {
              const thousands = Math.floor(remainder2 / 1000);
              const remainder3 = remainder2 % 1000;
              words += ' ' + convertHundreds(thousands) + ' Thousand';
              if (remainder3 > 0) {
                words += ' ' + convertHundreds(remainder3);
              }
            } else {
              words += ' ' + convertHundreds(remainder2);
            }
          }
        } else if (remainder >= 1000) {
          const thousands = Math.floor(remainder / 1000);
          const remainder2 = remainder % 1000;
          words += ' ' + convertHundreds(thousands) + ' Thousand';
          if (remainder2 > 0) {
            words += ' ' + convertHundreds(remainder2);
          }
        } else {
          words += ' ' + convertHundreds(remainder);
        }
      }
    }
    
    words = words.trim() + ' Rupees';
    
    if (paise > 0) {
      words += ' and ' + convertHundreds(paise).trim() + ' Paise';
    }
    
    return words + ' Only';
  };

  const handleDotMatrixPrint = async () => {
    console.log('Printing General Billing:', products);
    console.log('General Settings:', generalSetting);
    
    // Format date from "19-Nov-2025 5:29 PM" to "19/11/2025"
    const formatDate = (dateStr) => {
      if (!dateStr) return "";
      try {
        const dateObj = new Date(dateStr);
        if (isNaN(dateObj.getTime())) {
          return String(dateStr);
        }
        const day = String(dateObj.getDate()).padStart(2, '0');
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const year = dateObj.getFullYear();
        return `${day}/${month}/${year}`;
      } catch (error) {
        return String(dateStr ?? "");
      }
    };

    const data = {
      settings: {
        cpi: 12,         // Condensed printing
        width: generalSetting?.phPrinterWidth || 80,       // Full width in condensed mode
        indent: 0,
        printerType: generalSetting?.phPrinterType || "TVS",// "EPSON" or "TVS"

        feedInLines: generalSetting?.phFeedinLines || 0,
        feedInForward: generalSetting?.phFeedInForward || 0,
        feedOutLines: generalSetting?.phFeedOutLines || 0,
        feedOutForward: generalSetting?.phFeedOutForward || 0,
        returnValidityDays: generalSetting?.phReturnValidityDays || 0,
      },
    
      header: {
        firmName:
          generalSetting?.ShowFirmname
            ? `${generalSetting?.FirstName || ""} ${generalSetting?.LastName || ""}`.trim()
            : "",
    
        phones:
          generalSetting?.ShowFirmnumber
            ? [generalSetting?.MobileNo1, generalSetting?.MobileNo2].filter(Boolean)
            : [],
    
        address:
          generalSetting?.ShowAddress
            ? [
                generalSetting?.AddressLine1,
                generalSetting?.AddressLine2,
                generalSetting?.Place
              ].filter(Boolean)
            : [],
    
        workingTime:
          generalSetting?.ShowWorkingHourse && generalSetting?.WorkingTime
            ? String(generalSetting.WorkingTime)
            : "",
    
        gst:
          generalSetting?.ShowGst && generalSetting?.GstNo
            ? String(generalSetting.GstNo)
            : "",
    
        invoiceNumber: String(invoiceNumber ?? ""),
        date: formatDate(date),
        patientName: selectedPatient?.PatientName || "",
        age: selectedPatient?.Age ? String(selectedPatient.Age) : "",
        gender: selectedPatient?.Gender || "",
        doctor: selectedDoctor?.label || "",
        place: generalSetting?.Place || "",
      },
    
      products: validProducts.map((p, i) => {
        return {
          sr: i + 1,
          name: String(p.name || ""),
          qty: Number(p.qty ?? 0),
          rate: Number(p.rate ?? 0),
          amount: Number(p.amount ?? 0),
        };
      }),
    
      totals: {
        total: Number(calculateTotal() ?? 0),
        paid: Number(payingAmount ?? 0),
        balance: Number(balance_due ?? 0),
        balanceInWords: numberToWords(balance_due || 0)
      }
    };
      
    console.log('Print data:', data);
    
    try {
      await ipcRenderer.invoke("opprint-receipt", {
        printerName: sysInfo.OpBillPrinter,
        json: data
      });
      console.log('Dot matrix print sent successfully');
    } catch (err) {
      console.error('Dot matrix print failed:', err);
    }
  };

  useImperativeHandle(ref, () => ({
    handleRawTextPrint: handleDotMatrixPrint
  }));

  return (
    <div className="flex h-full bg-background">
        <div className="p-8 bg-gray-500">
            {/* Print Buttons */}
            <div className="mb-4 flex gap-4">
              <button
                onClick={handleDotMatrixPrint}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-semibold transition-colors"
              >
                Print (Dot Matrix Optimized)
              </button>
            </div>

            <div
                id="billDiv"
                style={{ page: "A5" }}
                className="h-[559px] w-[793px] bg-white flex flex-col justify-between"
            >
                <div className="p-2 flex-grow">
                    <div className="flex justify-between">
                        <div className="flex flex-col">
                            <p className="text-lg font-bold">{generalSetting?.FirstName}</p>
                            <p className="text-sm">{generalSetting?.LastName}</p>
                            <p className="text-sm">{generalSetting?.MobileNo1} , {generalSetting?.MobileNo2}</p>
                        </div>

                        <div className="border-l-4 border-black p-1">
                            <p className="text-sm">{generalSetting?.AddressLine1}</p>
                            <p className="text-sm">{generalSetting?.AddressLine2}</p>
                            <p className="text-sm">{generalSetting?.Place}</p>
                            <p className="text-sm">Working Time: {generalSetting?.WorkingTime}</p>
                        </div>
                    </div>

                    <p className="text-center text-black text-lg font-bold">CASH BILL</p>

                    <div className="flex mt-1 bg-white">
                        <div className="w-full">
                            <div className="flex justify-between w-full">
                                <div className="flex flex-row gap-5 items-center w-full justify-start">
                                    <p className="text-sm">Bill No:</p>
                                    <p className="text-black font-semibold">
                                        {invoiceNumber}
                                    </p>
                                    <svg ref={barcodeRef}></svg>
                                </div>
                                <div className='w-full text-right'>
                                    <p className="text-black text-xs font-semibold">{date}</p>
                                </div>
                            </div>

                            <div className="flex flex-col gap-1 mt-2">
                                <div className="flex justify-between">
                                    <div className="flex flex-row gap-5 w-full justify-start items-center">
                                        <p className="text-sm">Name :</p>
                                        <p className="text-black text-sm">
                                            {selectedPatient?.PatientName}
                                        </p>
                                    </div>
                                    <div className="flex flex-row gap-5 w-full justify-end items-center">
                                        <p className="text-sm">Age/Sex :</p>
                                        <p className="text-black text-sm">
                                            {selectedPatient?.Age}/{selectedPatient?.Gender}
                                        </p>
                                    </div>
                                </div>

                                <div className="flex justify-between">
                                    <div className="flex flex-row gap-5 w-full justify-start items-center">
                                        <p className="text-sm">Mobile Number :</p>
                                        <p className="text-black text-sm">
                                            +91-{selectedPatient?.MobileNo}
                                        </p>
                                    </div>
                                    <div className="flex flex-row gap-5 w-full justify-end items-center">
                                        <p className="text-sm">Referred By :</p>
                                        <p className="text-black text-sm">
                                            {selectedDoctor?.label}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-2">
                                {/* Fixed Table Header */}
                                <div className="flex w-full mb-1">
                                    <div className="w-[8%]">
                                        <p className="text-black font-semibold text-sm text-left">Sr.</p>
                                    </div>
                                    <div className="w-[52%]">
                                        <p className="text-black font-semibold text-sm text-left">Item Name</p>
                                    </div>
                                    <div className="w-[20%]">
                                        <p className="text-black font-semibold text-sm text-center">Qty</p>
                                    </div>
                                    <div className="w-[20%]">
                                        <p className="text-black font-semibold text-sm text-right">Amount</p>
                                    </div>
                                </div>
                                
                                <div className="w-full h-0.5 bg-black"></div>

                                {/* Fixed Product List */}
                                <div className="flex flex-col gap-1 mt-1 flex-grow w-full">
                                    {paginatedProducts.map((product, index) => {
                                        if (!product?.name) return null;
                                        const serialNumber =
                                            (currentPage - 1) * productsPerPage + (index + 1);

                                        return (
                                            <div
                                                key={index}
                                                className="flex w-full"
                                            >
                                                <div className="w-[8%]">
                                                    <p className="text-black font-semibold text-xs text-left">
                                                        {serialNumber}
                                                    </p>
                                                </div>
                                                <div className="w-[52%]">
                                                    <p className="text-black font-semibold text-xs text-left">
                                                        {product?.name}
                                                    </p>
                                                </div>
                                                <div className="w-[20%]">
                                                    <p className="text-black font-semibold text-xs text-center">
                                                        {product?.qty}
                                                    </p>
                                                </div>
                                                <div className="w-[20%]">
                                                    <p className="text-black font-semibold text-xs text-right">
                                                        {product?.amount}
                                                    </p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer Section with Line and Amounts */}
                <div className="w-full pb-5">
                    {/* Line above the amounts */}
                    <div className="w-full h-0.5 bg-black mb-1"></div>

                    {/* Amounts Section */}
                    <div className="flex flex-row gap-10 p-2 justify-between items-start">
                        {/* Printed By Line */}
                        <div className="flex flex-col justify-between w-full">
                        </div>

                        <div className="flex flex-col gap-0.5 text-right w-full">
                            <p className="text-sm font-bold text-black">Total</p>
                            <p className="text-sm font-bold text-black">Paid Amount</p>
                            <p className="text-sm font-bold text-black">Balance</p>
                        </div>
                        <div className="flex flex-col gap-0.5 text-right">
                            <p className="text-sm font-bold text-black">
                                {calculateTotal()}
                            </p>
                            <p className="text-sm font-bold text-black">
                                {payingAmount}
                            </p>
                            <p className="text-sm font-bold text-black">
                                {balance_due === 0 ? `${balance_due}.00` : balance_due}
                            </p>
                        </div>
                    </div>

                    {/* Line at the bottom */}
                    <div className="w-full h-0.5 bg-black"></div>
                </div>
            </div>
        </div>
    </div>
  );
});

export default NormalBillPrint;